import { Dispatch, SetStateAction, ChangeEvent } from 'react';
import classNames from 'classnames';
import { ReactComponent as UpIcon } from "./assets/up.svg";
import { ReactComponent as ClearIcon } from "./assets/clearBtn.svg";
import st from "./InputNumber.module.scss";

interface IInputNumberProps extends React.HTMLAttributes<HTMLDivElement> {
	name: string;
	readOnly?: boolean;
	size?: "min" | "std" | "large";
	isClearBtn?: boolean;
	value?: number | string | null;
	placeholder?: string;
	withoutArrows?: boolean;

	mode?: keyof typeof Modes;
	pattern?: string;
	step?: number;

	min?: number;
	max?: number;
	
	isInvalid?: boolean;
	onChangeValue: Dispatch<SetStateAction<number|string|null>>;
};

enum Modes {
	natural = 'natural',
	integer = 'integer',
	floating = 'floating',
	scientific = 'scientific',
};

const patternMapping = {
	[Modes.natural]: '(?:0|[1-9]\\d*)',
	[Modes.integer]: '[+\\-]?(?:0|[1-9]\\d*)',
	[Modes.floating]: '[+\\-]?([0-9])+(?:\\.\\d+)?',
	[Modes.scientific]: '[+\\-]?(?:0|[1-9]\\d*)(?:\\.\\d+)?(?:[eE][+\\-]?\\d+)?',
};

const InputNumber: React.FC<IInputNumberProps> = ( props ) => {

	const { name, value, className, readOnly = false, step = 1, pattern, min = -Infinity, max = Infinity, isClearBtn = true, placeholder = "", 
	withoutArrows = false, isInvalid = false, mode = Modes.natural, size = "std", onChangeValue, ...otherProps 
	} = props;

	const handleChange = ( event: ChangeEvent<HTMLInputElement> ) => {
		const val = replacer( event.target.value );
		if ( val === null ) onChangeValue(null);
		else if ( checkValid(val) ) {
			onChangeValue( val )
		}
	};

	/**
	 * Функция проверяет значение на валидность.
	 * (Максимум 15 цифр, без учета разделителя и знака)
	 * @param {*} val Значение
	 * @returns True - значение валидно, иначе False
	 */
	const checkValid = ( val: string ): boolean => {
		const length = val.replace(/[-|.|,]/g, '').length
		const regex = /^-?[0-9]{0,15}[.|,]?[0-9]{0,15}?$/;
		return val === "" || (length < 16 && regex.test(val));
	};

	/**
	 * Корректировка значения
	 * @param {*} val значение
	 * @returns откорректированное значение
	 */
	const replacer = ( val: string ) => {
		if (/^-?[.|,]\d+$/.test(val))
			val = val.includes('-') ? '-0' + val.substring(1) : '0' + value;

		return val.replace(',', '.');
	};

	const onChangeValueHandler = ( isDecrement: boolean = false ) => {
		const newValue = +(value ?? 0) + ( isDecrement ? -step : step ) ;
		if ( newValue >= min && newValue <= max ) {
			if ( !!onChangeValue) {
				onChangeValue( newValue );
			}
		}
	};

	const onClearHandler = ( e: React.MouseEvent<HTMLButtonElement, MouseEvent> ) => {
		e.preventDefault();
		e.stopPropagation();
		if ( !!onChangeValue && !readOnly ) {
			onChangeValue(null);
		}
	}; 

	const classes = classNames( st.control, className, st[size], { 
		[st.disabled]: !!readOnly,
		[st.invalidate]: isInvalid
	});

	return (
		<div className={classes}>
			{
				!withoutArrows &&
				<div className={st.buttons}>
					<button type='button' onClick={ () => onChangeValueHandler() } disabled={readOnly}>
						<UpIcon />
					</button>
					<button type='button' onClick={ () => onChangeValueHandler(true) } disabled={readOnly}>
						<UpIcon />
					</button>
				</div>
			}
			<input
				{...otherProps}
				type='text'
				name={name}
				pattern={ pattern ? pattern : patternMapping[mode] }
				step={step}
				placeholder={placeholder} 
				disabled={readOnly} 
				onChange={handleChange}
				value={value ?? 0}
			/>
			{
				( isClearBtn && readOnly ) &&
				<button type='button' disabled={ !value } onClick={onClearHandler}>
					<ClearIcon />
				</button>
			}
		</div>
	);
};

export default InputNumber;
